import { useEffect, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import type { KnowledgeDocument } from '../../api';
import { useKnowledgeBuilderColors } from '../../theme/knowledgeBuilderColors';

export const BinaryDocumentPreview = ({
  document,
}: {
  document: KnowledgeDocument;
}) => {
  const colors = useKnowledgeBuilderColors();
  if (/\.png$/i.test(document.path)) {
    return (
      <PreviewShell>
        <img
          src={document.content}
          alt={document.title}
          style={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: 560,
            marginTop: 12,
            objectFit: 'contain',
            border: `1px solid ${colors.border.default}`,
            borderRadius: 14,
          }}
        />
      </PreviewShell>
    );
  }

  if (/\.pdf$/i.test(document.path)) {
    return (
      <PreviewShell fill>
        <embed
          src={document.content}
          type="application/pdf"
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            border: `1px solid ${colors.border.default}`,
            borderRadius: 8,
          }}
        />
      </PreviewShell>
    );
  }

  if (/\.docx$/i.test(document.path)) {
    return <DocxPreview document={document} />;
  }

  return (
    <PreviewShell>
      <Typography variant="body2" style={{ marginTop: 12, color: colors.text.secondary }}>
        Preview isn't available for this file type.
      </Typography>
      <DownloadLink document={document} />
    </PreviewShell>
  );
};

export function DocxPreview({ document }: { document: KnowledgeDocument }) {
  const colors = useKnowledgeBuilderColors();
  const [html, setHtml] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    setHtml(undefined);
    setError(undefined);

    Promise.all([import('mammoth'), import('dompurify')])
      .then(([mammoth, dompurify]) => {
        if (cancelled) {
          return;
        }
        const arrayBuffer = dataUrlToArrayBuffer(document.content);
        return mammoth.default
          .convertToHtml({ arrayBuffer })
          .then(result => {
            if (!cancelled) {
              setHtml(dompurify.default.sanitize(result.value));
            }
          });
      })
      .catch(() => {
        if (!cancelled) {
          setError('Unable to render this Word document.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [document.content]);

  if (error) {
    return (
      <PreviewShell>
        <Typography variant="body2" style={{ marginTop: 12, color: '#f87171' }}>
          {error}
        </Typography>
        <DownloadLink document={document} />
      </PreviewShell>
    );
  }

  if (!html) {
    return (
      <PreviewShell>
        <Typography variant="body2" style={{ marginTop: 12, color: colors.text.secondary }}>
          Rendering document…
        </Typography>
      </PreviewShell>
    );
  }

  return (
    <div
      className="knowledge-builder-scrollbar"
      style={{ height: '100%', overflow: 'auto', background: colors.bg.root, padding: 16 }}
    >
      <div
        style={{
          maxWidth: 820,
          margin: '0 auto',
          padding: '40px 56px',
          background: '#ffffff',
          color: '#1f2937',
          borderRadius: 4,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
        }}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

function DownloadLink({ document }: { document: KnowledgeDocument }) {
  const colors = useKnowledgeBuilderColors();
  return (
    <a
      href={document.content}
      download={document.title}
      style={{
        display: 'inline-block',
        marginTop: 12,
        color: colors.accent.blue,
        fontSize: 12,
      }}
    >
      Download {document.title}
    </a>
  );
}

function PreviewShell({
  children,
  fill,
}: {
  children: React.ReactNode;
  fill?: boolean;
}) {
  const colors = useKnowledgeBuilderColors();
  return (
    <div
      className="knowledge-builder-scrollbar"
      style={{
        height: '100%',
        padding: 16,
        overflow: 'auto',
        background: colors.bg.root,
        boxSizing: 'border-box',
        display: fill ? 'flex' : undefined,
        flexDirection: fill ? 'column' : undefined,
      }}
    >
      {children}
    </div>
  );
}

function dataUrlToArrayBuffer(dataUrl: string): ArrayBuffer {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}
